import React from 'react';
import { ArrowUpRight, Heart } from 'lucide-react';
import { useLang } from '../i18n/LangContext';
import Reveal from './Reveal';

const FOUNDATION_LOGO = '/images/community/funtexotli-logo.jpg';
const FOUNDATION_LINK = 'https://www.instagram.com/funtexotli?igsh=MThzZGc2bjBkMTg3OQ==';

export default function Community() {
  const { t } = useLang();
  const pillars = t('community.pillars');

  return (
    <section
      id="community"
      data-testid="community-section"
      className="relative py-24 md:py-40 border-t border-[#27272A] bg-[#0B0B0D]"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 mb-16">
          <div className="md:col-span-4">
            <Reveal className="section-index">{t('community.index')}</Reveal>
            <Reveal className="reveal-delay-1 mt-3 font-mono text-[10px] tracking-[0.3em] uppercase text-[#C8102E]">
              {t('community.eyebrow')}
            </Reveal>
          </div>
          <Reveal as="h2" className="md:col-span-8 h-section text-[#F5F5F2] reveal-delay-1">
            {t('community.title')}
          </Reveal>
        </div>

        {/* Body — split layout: foundation card left / narrative right */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          {/* Foundation card */}
          <Reveal className="lg:col-span-5 reveal-delay-2">
            <div className="border border-[#27272A] bg-[#0A0A0B] overflow-hidden">
              {/* Logo block — sits on native black, no plate */}
              <div className="aspect-[16/10] bg-black flex items-center justify-center p-8 border-b border-[#27272A] relative">
                <img
                  src={FOUNDATION_LOGO}
                  alt="Fundación Piltsintli Texotli"
                  className="max-h-full max-w-full object-contain"
                  loading="lazy"
                />
                <div className="absolute top-4 left-4 font-mono text-[10px] tracking-[0.3em] uppercase text-[#71717A]">
                  {t('community.foundation_label')}
                </div>
              </div>

              <div className="p-7 md:p-8">
                <div className="font-display text-2xl md:text-3xl tracking-tight text-[#F5F5F2]">
                  {t('community.foundation_name')}
                </div>
                <div className="mt-2 italic text-sm text-[#A1A1AA]">
                  &ldquo;{t('community.foundation_tag')}&rdquo;
                </div>

                <div className="mt-7 pt-6 border-t border-[#27272A]">
                  <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#71717A] mb-2">
                    {t('community.status_label')}
                  </div>
                  <div className="text-sm text-[#F5F5F2]">{t('community.status_value')}</div>
                </div>

                <a
                  href={FOUNDATION_LINK}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  data-testid="foundation-instagram-link"
                  className="mt-7 inline-flex items-center gap-3 group text-[#F5F5F2] hover:text-[#C8102E] transition-colors"
                >
                  <span className="font-mono text-[11px] tracking-[0.22em] uppercase">
                    {t('community.link_label')}
                  </span>
                  <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                </a>
              </div>
            </div>
          </Reveal>

          {/* Narrative */}
          <div className="lg:col-span-7">
            <Reveal className="reveal-delay-2">
              <div className="flex items-start gap-4">
                <Heart size={20} className="text-[#C8102E] mt-1 shrink-0" />
                <p className="text-lg md:text-xl text-[#F5F5F2]/90 leading-relaxed">
                  {t('community.lead')}
                </p>
              </div>
            </Reveal>

            <Reveal className="reveal-delay-3 mt-8 text-base md:text-lg text-[#A1A1AA] leading-relaxed">
              {t('community.body_1')}
            </Reveal>

            <Reveal className="reveal-delay-4 mt-6 text-base md:text-lg text-[#A1A1AA] leading-relaxed">
              {t('community.body_2')}
            </Reveal>

            {/* Pillars */}
            <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-px bg-[#27272A] border border-[#27272A]">
              {pillars.map((p, i) => (
                <Reveal key={p.k} className={`reveal-delay-${(i % 4) + 1}`}>
                  <div data-testid={`community-pillar-${i}`} className="bg-[#0F0F11] p-6 h-full">
                    <span className="font-mono text-[10px] tracking-[0.3em] text-[#C8102E]">
                      0{i + 1}
                    </span>
                    <div className="mt-4 font-display text-lg md:text-xl tracking-tight text-[#F5F5F2]">
                      {p.k}
                    </div>
                    <p className="mt-2 text-sm text-[#A1A1AA] leading-relaxed">{p.v}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
